import React, { useState } from 'react';
import { Modal, Button, Input, Select, Textarea, FieldLabel } from '../ui';
import { formatPrice } from '../../utils/formatters';
import { refundPreference } from '../../constants/statusConfig';
import { refundOrder } from '../../services/apiClient';
import logger from '../../utils/logger';

// Reembolsar uma encomenda, total ou parcialmente.
//
// O parcial é o caso que interessa no dia a dia: faltou uma sobremesa, veio uma
// coisa trocada, o cliente ligou a reclamar. Devolver 4,50€ num minuto evita uma
// crítica de uma estrela.
//
// Quando o cliente sinalizou na app o que prefere, o método vem **já escolhido
// nessa preferência**. Se o valor por omissão não fosse o que ele pediu, a
// sinalização toda era decoração.
//
// Só conta o que foi efetivamente pago (o `total`). Numa encomenda paga em
// parte com voucher, a parte do voucher não conta para nada: o "Tudo" devolve
// o dinheiro pago, em crédito ou para o banco.
//
// A exceção é a encomenda paga *toda* com crédito na conta (total 0,00€): aí o
// que conta é o crédito usado, que volta como um voucher novo do mesmo valor,
// e a opção do banco nem aparece. Sem isto aparecia "Tudo (0,00€)".
// O fim do código, que é o que se diz ao telefone: em "INOVA-DEV-88-DFD87D" é
// "DFD87D". O princípio é igual em todos os vouchers do cliente.
export const fimDoCodigo = (code) => String(code || '').split('-').pop();

const RefundModal = ({ open, order, token, apiUrl, onClose, onDone }) => {
  const dinheiro = Number(order?.total) || 0;
  const credito = Number(order?.credit_used) || 0;
  const soCredito = credito > 0 && dinheiro <= 0;
  const total = soCredito ? credito : dinheiro;
  const preferido = soCredito ? 'voucher' : order?.refund_preference;

  const [scope, setScope] = useState('total');
  const [amount, setAmount] = useState(String(total.toFixed(2)));
  const [method, setMethod] = useState(preferido || 'bank');
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [result, setResult] = useState(null);

  if (!open || !order) return null;

  const valorParcial = Number(String(amount).replace(',', '.'));
  const parcialInvalido =
    scope === 'partial' && (!Number.isFinite(valorParcial) || valorParcial <= 0);

  const handleRefund = async () => {
    setError(null);
    setBusy(true);
    logger.userAction('Refund order', { orderId: order.order_id, scope, method });
    try {
      const data = await refundOrder(apiUrl, token, order.order_id, {
        // Sem valor, o servidor devolve tudo o que ainda falta — que é mais
        // seguro do que mandar daqui um total que pode já ter sido reembolsado
        // em parte.
        amount: scope === 'partial' ? valorParcial : undefined,
        method,
        reason: reason.trim() || undefined,
      });
      setResult(data);
      // A resposta inteira, e não só o id: traz a encomenda como ficou, que é
      // o que põe o cartão na secção certa sem esperar pelo próximo refresh.
      onDone?.(data);
    } catch (err) {
      logger.error('Refund failed', { orderId: order.order_id, error: err.message });
      // Dentro do modal e não num alert: o staff está ao telefone e precisa de
      // ler o motivo sem perder o que já preencheu.
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const fechar = () => {
    setResult(null);
    setError(null);
    onClose?.();
  };

  return (
    <Modal
      open={open}
      onClose={fechar}
      size="sm"
      title={`Reembolsar pedido #${order.order_id}`}
      subtitle={`${order.customer_name || 'Cliente'} · ${formatPrice(total)}`}
      footer={
        result ? (
          <Button onClick={fechar}>Fechar</Button>
        ) : (
          <>
            <Button variant="secondary" onClick={fechar} disabled={busy}>
              Cancelar
            </Button>
            <Button variant="danger" onClick={handleRefund} loading={busy} disabled={parcialInvalido}>
              Reembolsar
            </Button>
          </>
        )
      }
    >
      {result ? (
        <div className="space-y-3 text-sm">
          <p className="font-semibold text-status-completed">
            {result.voucher
              ? `Crédito de ${formatPrice(result.amount)} dado ao cliente.`
              : `Reembolso de ${formatPrice(result.amount)} feito.`}
          </p>

          {result.voucher && (
            // Logo a seguir à confirmação e em grande: o staff está ao telefone
            // e diz ao cliente "é o voucher que termina em …". O cliente vê o
            // mesmo código na app, em Pontos.
            <div className="rounded-xl border-2 border-status-completed bg-status-completed-soft px-4 py-3 text-center">
              <p className="text-xs uppercase tracking-wide text-muted">Voucher novo</p>
              <p className="mt-1 text-lg">
                termina em{' '}
                <span className="font-mono text-2xl font-bold">{fimDoCodigo(result.voucher.code)}</span>
              </p>
              <p className="mt-1 font-mono text-sm">{result.voucher.code}</p>
              <p className="mt-2 text-xs text-muted">
                {formatPrice(result.voucher.value ?? result.amount)} · sem prazo de validade · já
                está na conta do cliente.
              </p>
            </div>
          )}

          {/* O cartão vai desaparecer dos pedidos ativos assim que este modal
              fechar. Dizê-lo aqui evita a procura que se segue a um cartão que
              some sem explicação. */}
          {result.fully_refunded ? (
            <p className="rounded-xl bg-status-pending-soft px-3 py-2">
              O pedido ficou marcado como <span className="font-bold">Reembolsado</span> e saiu
              dos pedidos ativos.
              <span className="block text-muted text-xs mt-1">
                Está no histórico, no topo, a cores durante a próxima hora.
              </span>
            </p>
          ) : (
            <p className="text-muted text-xs">
              Ainda faltam {formatPrice(result.remaining)} por devolver — o pedido continua
              onde estava.
            </p>
          )}

          {result.refund?.reference && (
            <p className="rounded-xl bg-surface-2 px-3 py-2">
              Referência MB WAY: <span className="font-bold">{result.refund.reference}</span>
              <span className="block text-muted text-xs mt-1">
                O cliente pode usá-la para seguir o reembolso no banco.
              </span>
            </p>
          )}

          {/* O aviso serve os dois métodos: no `bank` falhou o registo do
              reembolso, no `voucher` falhou o saldo que impede um segundo
              crédito sobre a mesma encomenda. A consequência é diferente e o
              staff precisa de saber qual é. */}
          {result.recorded_in_woocommerce === false && (
            <p className="rounded-xl bg-status-pending-soft px-3 py-2 text-status-pending">
              {result.method === 'voucher'
                ? 'O crédito foi dado ao cliente, mas não ficou registado na encomenda — ela vai continuar a aparecer por reembolsar. Confirma no WooCommerce antes de devolver mais alguma coisa.'
                : 'O dinheiro foi devolvido, mas o registo na encomenda falhou. Confirma no WooCommerce.'}
            </p>
          )}

          {result.points_reversed > 0 && (
            <p className="text-muted text-xs">
              Foram retirados {result.points_reversed} pontos ao cliente.
            </p>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {order.refund_request_status === 'requested' && (
            <p className="rounded-xl bg-status-pending-soft px-3 py-2 text-sm">
              O cliente pediu <span className="font-semibold">
                {refundPreference[preferido] || 'reembolso'}
              </span>.
              {order.refund_request_reason && (
                <span className="block italic mt-1">"{order.refund_request_reason}"</span>
              )}
            </p>
          )}

          <Select
            id="refund-scope"
            label="O que devolver"
            value={scope}
            onChange={(e) => setScope(e.target.value)}
          >
            <option value="total">Tudo ({formatPrice(total)})</option>
            <option value="partial">Só uma parte</option>
          </Select>

          {scope === 'partial' && (
            <FieldLabel label="Valor a devolver (€)" required htmlFor="refund-amount">
              <Input
                id="refund-amount"
                type="number"
                step="0.01"
                min="0.01"
                max={total}
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
                autoFocus
              />
            </FieldLabel>
          )}

          <Select
            id="refund-method"
            label="Como devolver"
            value={method}
            onChange={(e) => setMethod(e.target.value)}
          >
            <option value="voucher">Crédito na conta (fica disponível já)</option>
            {!soCredito && <option value="bank">Dinheiro de volta (5 a 10 dias úteis)</option>}
          </Select>

          {soCredito && (
            <p className="rounded-xl bg-status-confirmed-soft px-3 py-2 text-sm text-foreground">
              {`Paga toda com crédito na conta (${formatPrice(credito)}): só pode voltar em crédito, num voucher novo do mesmo valor.`}
            </p>
          )}

          <Textarea
            id="refund-reason"
            label="Motivo (fica na encomenda)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            placeholder="Ex.: faltou a sobremesa"
          />

          {error && (
            <p className="rounded-xl bg-status-canceled-soft px-3 py-2 text-sm text-status-canceled">
              {error}
            </p>
          )}
        </div>
      )}
    </Modal>
  );
};

export default RefundModal;
